import React from 'react';
import { Link } from 'react-router-dom';
import { GitBranch, CheckCircle, ArrowRight, Zap, RefreshCw, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import SEO from '../components/SEO';

const DevOpsSolutions = () => {
  const services = [
    {
      icon: Target,
      title: 'Strategy & Analysis',
      description: 'Analyze current processes and strategize DevOps practices tailored to your business needs'
    },
    {
      icon: GitBranch,
      title: 'CI/CD Implementation',
      description: 'Build and deploy automated pipelines for streamlined build, test, and deploy workflows'
    },
    {
      icon: RefreshCw,
      title: 'Continuous Integration',
      description: 'Implement continuous integration practices to accelerate development cycles'
    },
    {
      icon: Zap,
      title: 'Automation',
      description: 'Automate repetitive tasks and workflows to improve efficiency and reduce errors'
    }
  ];

  const benefits = [
    '50% faster release cycles',
    'Streamlined build, test, deploy workflows',
    'Experienced DevOps engineers',
    'Modern tools & technologies',
    'Reduced deployment failures',
    'Improved team collaboration'
  ];

  const results = [
    { metric: '50%', label: 'Faster Releases' },
    { metric: '40%', label: 'Cost Reduction' },
    { metric: '99.98%', label: 'Uptime' }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="DevOps Solutions — CI/CD Automation & Pipelines | NIVA Digital AI"
        description="Comprehensive DevOps automation services including CI/CD pipelines, infrastructure as code, containerization, and release management."
        keywords="DevOps automation, CI/CD pipelines, infrastructure as code, DevOps consulting, continuous integration, continuous deployment"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/services/devops`}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <GitBranch size={20} />
                <span>DevOps Solutions</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Accelerate Your
                <span className="block text-red-600 mt-2">Delivery Cycles</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Analyze, strategize, and implement DevOps practices tailored to your business needs. Accelerate delivery cycles with streamlined workflows.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                  Get Started <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-green-100 to-emerald-50 rounded-2xl p-8 shadow-2xl">
                <GitBranch className="text-green-600 mx-auto" size={200} strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our DevOps Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive DevOps solutions to transform your development and deployment processes
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300 hover:shadow-xl">
                  <CardHeader>
                    <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-red-600" size={28} />
                    </div>
                    <CardTitle className="text-2xl mb-2">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Proven Results
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real metrics from our client engagements
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {results.map((result, index) => (
              <Card key={index} className="border-2 border-red-600 text-center">
                <CardContent className="pt-8">
                  <p className="text-5xl font-bold text-red-600 mb-2">{result.metric}</p>
                  <p className="text-gray-600 text-lg">{result.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                    <p className="text-gray-700 font-medium">{benefit}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Expert Team */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="bg-gradient-to-br from-green-100 to-emerald-50 rounded-2xl p-12 shadow-2xl">
                <div className="space-y-6">
                  <div className="bg-white p-6 rounded-lg shadow">
                    <GitBranch className="text-green-600 mb-2" size={32} />
                    <h4 className="font-semibold">Modern Tools</h4>
                    <p className="text-sm text-gray-600 mt-1">Latest DevOps technologies</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <Zap className="text-green-600 mb-2" size={32} />
                    <h4 className="font-semibold">Fast Deployment</h4>
                    <p className="text-sm text-gray-600 mt-1">Rapid release cycles</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Experienced DevOps Engineers
              </h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Our teams consist of experienced engineers proficient in modern tools, technologies, and frameworks.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Expert Team</h3>
                    <p className="text-gray-600">Certified DevOps professionals with years of experience</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Modern Stack</h3>
                    <p className="text-gray-600">Proficient in latest DevOps tools and best practices</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Continuous Learning</h3>
                    <p className="text-gray-600">Always up-to-date with emerging technologies and practices</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your DevOps?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Schedule a consultation to discuss your DevOps transformation
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Contact Us <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DevOpsSolutions;